import { isAHoliday } from '@18f/us-federal-holidays';
import {
	addWeeks,
	addMonths,
	addYears,
	addDays,
	addBusinessDays,
	subDays,
	subMonths,
	formatDistanceToNow,
	differenceInYears,
	differenceInMonths,
	startOfTomorrow,
	startOfMonth,
	endOfMonth,
	isSameDay,
	isValid,
	format,
	parse,
	parseISO,
	differenceInDays,
	isWeekend,
	isToday,
	isTomorrow,
} from 'date-fns';

// -----------------------------------------------------------------------------
// Private functions
// -----------------------------------------------------------------------------

const _getPreciseAge = (dateOfBirth: Date): number => {
	const months = differenceInMonths(new Date(), dateOfBirth);
	return Math.max(months, 0) / 12;
};

const _formatDate = (date: Date, template: string): string => {
	return format(date, template);
};

// -----------------------------------------------------------------------------
// Custom date helpers
// -----------------------------------------------------------------------------

export const getAge = (
	dateOfBirth?: Date | string,
	precise = false,
): number => {
	const dobObj = parseDate(dateOfBirth) || new Date();

	if (precise) {
		return _getPreciseAge(dobObj);
	}

	const years = differenceInYears(new Date(), dobObj);

	// normalize since it's possible to get -0
	return Math.max(years, 0);
};

export const getExactAge = (dateOfBirth?: Date | string): number =>
	getAge(dateOfBirth, true);

export const getTomorrow = (): Date => startOfTomorrow();

export const dateFromNow = (
	date: Date,
	includeSeconds = false,
): string => {
	return formatDistanceToNow(date, { includeSeconds });
};

export const getFormattedDateString = (
	date: Date,
	template = 'MMMM do, yyyy',
): string => {
	return _formatDate(date, template);
};

export const getISOString = (date: Date): string => {
	return _formatDate(date, "yyyy-MM-dd'T'HH:mm:ss.SSSxxx");
};

export const getDateString = (date: Date | string): string => {
	const dateObj = typeof date === 'string' ? parseISO(date) : date;
	return _formatDate(dateObj, 'yyyy-MM-dd');
};

export const parseDate = (
	date?: Date | string,
	formatString?: string,
	fallbackDate?: Date | null,
): Date | undefined => {
	if (formatString) {
		return parse(date as string, formatString, fallbackDate || new Date());
	}

	if (typeof date === 'string') {
		return parseISO(date);
	}

	return date;
};

export const isDateValid = (date: Date): boolean => {
	return isValid(date);
};

export const getFormattedReminderDate = (date: Date): string => {
	return format(date, 'MM/dd/yyyy');
};

export const formatDate = (
	date: Date | string,
	formatString = 'yyyy-MM-dd',
): string => {
	const dateObj = typeof date === 'string' ? parseISO(date) : date;
	return format(dateObj, formatString);
};

export const shiftHolidays = {
	shiftSaturdayHolidays: true,
	shiftSundayHolidays: true,
};

export const addBusinessDaysAndHolidays = (
	date: Date,
	days: number,
): Date => {
	let newDay = date;
	let remainingDays = days;

	while (remainingDays > 0) {
		newDay = addDays(newDay, 1);
		if (!isWeekend(newDay) && !isAHoliday(newDay, shiftHolidays)) {
			remainingDays = remainingDays - 1;
		}
	}

	return newDay;
};

/** If the date is today, formats local date to string like "Today, Jan 28".
 *  If the date is tomorrow, formats local date to string like "Tomorrow, Jan 28".
 *  If the date is any other day, formats local date to string like Tuesday, Jan 28". */
export const formatTodayTomorrowOrShortMonthDay = (
	date: Date | string,
): string => {
	const parsedDate = typeof date === 'string' ? parseISO(date) : date;
	let formatTemplate = 'eeee, MMM d';
	if (isToday(parsedDate)) {
		formatTemplate = "'Today,' MMM d";
	} else if (isTomorrow(parsedDate)) {
		formatTemplate = "'Tomorrow,' MMM d";
	}
	return formatDate(parsedDate, formatTemplate);
};
// -----------------------------------------------------------------------------
//  date utils re-exported directly from date-fns
// -----------------------------------------------------------------------------

export {
	isSameDay,
	addWeeks,
	addMonths,
	addYears,
	addDays,
	addBusinessDays,
	subDays,
	subMonths,
	startOfMonth,
	endOfMonth,
	differenceInDays,
	format,
	parse,
	isWeekend,
};
